import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Box, Tabs, Tab,
  List, ListItem, ListItemIcon, ListItemText, Button,
  Avatar, Chip
} from '@mui/material';
import {
  CheckCircle, AttachMoney, Photo, Warning,
  Message, Build, Schedule, TrendingUp, Description,
  Cancel
} from '@mui/icons-material';
import { status } from '../../theme/colors';

const ICON_MAP = {
  milestone_completed:        <CheckCircle sx={{ color: '#43B581' }} />,
  milestone_approved:         <CheckCircle sx={{ color: '#43B581' }} />,
  milestone_report_submitted: <Build sx={{ color: status.info }} />,
  milestone_changes_requested:<Schedule sx={{ color: '#FAA61A' }} />,
  milestone_rejected:         <Cancel sx={{ color: '#F04747' }} />,
  payment_released:           <AttachMoney sx={{ color: '#FAA61A' }} />,
  contractor_message:         <Message sx={{ color: status.info }} />,
  photo_uploaded:             <Photo sx={{ color: '#F04747' }} />,
  document_uploaded:          <Description sx={{ color: '#999' }} />,
  verification_requested:     <CheckCircle sx={{ color: '#00BCD4' }} />,
  verification_completed:     <CheckCircle sx={{ color: '#43B581' }} />,
  dispute_raised:             <Warning sx={{ color: '#F04747' }} />,
  dispute_resolved:           <CheckCircle sx={{ color: '#43B581' }} />,
  project_started:            <TrendingUp sx={{ color: '#43B581' }} />,
  project_completed:          <CheckCircle sx={{ color: '#43B581' }} />,
};

const PROJECT_TYPES = [
  'milestone_completed', 'milestone_approved', 'milestone_report_submitted',
  'milestone_changes_requested', 'milestone_rejected',
  'verification_requested', 'verification_completed',
  'photo_uploaded', 'document_uploaded',
  'project_started', 'project_completed',
];

const PAYMENT_TYPES = ['payment_released'];

/**
 * Activity Feed Component
 * Shows real project activity from project_activity_feed table
 */
const ActivityFeed = ({ activities = [] }) => {
  const [selectedTab, setSelectedTab] = useState(0);

  // The dashboard API maps activity_type -> type and created_at -> timestamp;
  // accept both shapes so raw feed rows keep working too.
  const activityType = (a) => a.type || a.activity_type;
  const activityTime = (a) => a.timestamp || a.created_at;

  const getFilteredActivities = () => {
    if (selectedTab === 1) return activities.filter(a => PROJECT_TYPES.includes(activityType(a)));
    if (selectedTab === 2) return activities.filter(a => PAYMENT_TYPES.includes(activityType(a)));
    return activities;
  };

  const filteredActivities = getFilteredActivities();

  const formatTime = (time) => {
    const date = new Date(time);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getIcon = (activityType) => {
    return ICON_MAP[activityType] || <TrendingUp sx={{ color: '#999' }} />;
  };

  if (activities.length === 0) {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Recent Activity
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TrendingUp sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No activity yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activity will appear here as your projects progress
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Activity
          </Typography>
          <Chip
            label={`${filteredActivities.length} items`}
            size="small"
            sx={{ bgcolor: '#f0f0f0' }}
          />
        </Box>

        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Projects" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Payments" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredActivities.slice(0, 10).map((activity) => (
            <ListItem
              key={activity.id}
              sx={{
                borderRadius: 1,
                mb: 1,
                bgcolor: activity.is_read ? '#fafafa' : '#f0f7ff',
                '&:hover': {
                  bgcolor: '#f0f0f0'
                }
              }}
            >
              <ListItemIcon>
                <Avatar sx={{ width: 40, height: 40, bgcolor: 'white' }}>
                  {getIcon(activityType(activity))}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {activity.title}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {formatTime(activityTime(activity))}
                      {(activity.actorName || activity.actor_name) ? ` - ${activity.actorName || activity.actor_name}` : ''}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>

        {filteredActivities.length > 10 && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Load More Activity
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
